import { Theme } from "@/components/theme/themeProvider"
import type { UserCellVariants } from "@/components/ui/layout/table/userCell"
import type { BoxDetailsEntity } from "@/modules/box/entities/boxEntity"
import { word } from "@/modules/core/utils/i18n"
import { OutOfBandTransactionStatus } from "@/modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import type { TransactionDetailsEntity } from "@/modules/finances/entities/transactionDetailsEntity"
import {
  getOutOfBandTransactionColorStatus,
  getOutOfBandTransactionStatusWording,
  getTransactionColorStatus,
  getTransactionErrorAction,
  getTransactionErrorActionWording,
  getTransactionErrorReasonWording,
  getTransactionStatusWording,
} from "@/modules/finances/transactions/utils/transactionUtils"
import { PaymentMethodType, TransactionStatus } from "@/modules/finances/types/financesType"
import { isInArray } from "@/modules/utils/isInArray"

export type InvoicePaymentDetailsModel = {
  colorStatus: UserCellVariants
  wordingStatus: string | undefined
  reasonWording: string | undefined
  errorActionWording: string | undefined
  errorAction: "contactAthlete" | "contactSupport" | undefined
  outOfBandPaymentMethodLabel: string | undefined
  isOutOfBandPaymentEnabled: boolean
  shouldDisplayReason: boolean
  shouldDisplayHistory: boolean
  shouldDisplayOutOfBandPayment: boolean
  shouldDisplayPaymentMethod: boolean
  shouldDisplayPayoutDate: boolean
  paymentMethodLabel: string
  paymentMethodDetails: string
}

export function createInvoicePaymentDetailsModel(
  transactionDetails: TransactionDetailsEntity,
  currentBox: BoxDetailsEntity | null,
  isPaymentAtCounterToggleActive: boolean,
  theme: Theme,
): InvoicePaymentDetailsModel {
  const isOutOfBandPaymentEnabled =
    ((transactionDetails.status === TransactionStatus.FAILED && isPaymentAtCounterToggleActive) ||
      transactionDetails.outOfBandStatus === OutOfBandTransactionStatus.PENDING_VALIDATION ||
      transactionDetails.outOfBandStatus === OutOfBandTransactionStatus.PAST_DUE) &&
    !!currentBox

  const shouldDisplayPaymentMethod =
    !!transactionDetails.paymentMethodDetail && !!transactionDetails.paymentMethodDetail.last4Digits

  const shouldDisplayHistory =
    (transactionDetails.status === TransactionStatus.FAILED &&
      !!transactionDetails.history &&
      !!transactionDetails.history[0] &&
      !!transactionDetails.history[0].errorCode) ||
    transactionDetails.status === TransactionStatus.REFUNDED

  const shouldDisplayOutOfBandPayment =
    !!transactionDetails.outOfBandStatus &&
    isInArray(
      [
        OutOfBandTransactionStatus.PAID,
        OutOfBandTransactionStatus.CARD,
        OutOfBandTransactionStatus.CASH,
        OutOfBandTransactionStatus.CHECK,
      ],
      transactionDetails.outOfBandStatus,
    )

  const outOfBandPaymentMethodLabel =
    transactionDetails.outOfBandStatus === OutOfBandTransactionStatus.CARD
      ? word(`finances.transaction.outOfBandPayment.card`)
      : transactionDetails.outOfBandStatus === OutOfBandTransactionStatus.CASH
        ? word(`finances.transaction.outOfBandPayment.cash`)
        : word(`finances.transaction.outOfBandPayment.check`)

  const colorStatus = transactionDetails.outOfBandStatus
    ? getOutOfBandTransactionColorStatus(transactionDetails)
    : getTransactionColorStatus(transactionDetails, theme)

  const wordingStatus = transactionDetails.outOfBandStatus
    ? getOutOfBandTransactionStatusWording(transactionDetails)
    : getTransactionStatusWording(transactionDetails)

  const shouldDisplayReason =
    transactionDetails.status === TransactionStatus.DISPUTED || // Display reason for disputed transactions
    (((transactionDetails.status === TransactionStatus.PENDING && // Display reason for failed transactions with card payment and history
      transactionDetails.paymentMethodDetail?.type === PaymentMethodType.CARD) ||
      transactionDetails.status === TransactionStatus.FAILED) && // Display reason for failed transactions with other payment methods and history
      !!transactionDetails.history &&
      !!transactionDetails.history[0] &&
      !!transactionDetails.history[0].errorCode)

  const reasonWording = shouldDisplayReason
    ? transactionDetails.status === TransactionStatus.DISPUTED
      ? word("finances.details.status.disputed.label")
      : transactionDetails.history && transactionDetails.history[0] && transactionDetails.history[0].errorCode
        ? getTransactionErrorReasonWording(transactionDetails.history[0].errorCode)
        : undefined
    : undefined

  const errorActionWording = shouldDisplayReason
    ? transactionDetails.status === TransactionStatus.DISPUTED
      ? word("finances.details.status.disputed.action")
      : transactionDetails.history && transactionDetails.history[0] && transactionDetails.history[0].errorCode
        ? getTransactionErrorActionWording(transactionDetails.history[0].errorCode)
        : undefined
    : undefined

  const errorAction = shouldDisplayReason
    ? transactionDetails.status === TransactionStatus.DISPUTED
      ? "contactSupport"
      : transactionDetails.history && transactionDetails.history[0] && transactionDetails.history[0].errorCode
        ? getTransactionErrorAction(transactionDetails.history[0].errorCode)
        : undefined
    : undefined

  const paymentMethodLabel =
    transactionDetails.paymentMethodDetail?.type === PaymentMethodType.CARD
      ? word("finances.page.table.paymentMethod.card")
      : transactionDetails.paymentMethodDetail?.type === PaymentMethodType.SEPA_DEBIT
        ? "IBAN"
        : word("finances.page.table.header.paymentMethod")

  const paymentMethodDetails = `${
    transactionDetails.paymentMethodDetail?.type === PaymentMethodType.CARD ? "************" : "***********************"
  } ${transactionDetails.paymentMethodDetail?.last4Digits}`

  const shouldDisplayPayoutDate = !!transactionDetails.payout?.payoutAt

  return {
    colorStatus,
    wordingStatus,
    reasonWording,
    isOutOfBandPaymentEnabled,
    shouldDisplayReason,
    shouldDisplayPaymentMethod,
    shouldDisplayHistory,
    shouldDisplayOutOfBandPayment,
    shouldDisplayPayoutDate,
    outOfBandPaymentMethodLabel,
    paymentMethodLabel,
    paymentMethodDetails,
    errorAction,
    errorActionWording,
  }
}
